'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NavigationMenu, NavigationMenuList } from '../../ui/navigation-menu.jsx';
import { MenuItem } from './menu-item';
/** Mirrors Radix's own `delayDuration` so a parent entry opens on hover with its original feel. */
var HOVER_OPEN_DELAY_MS = 200;
/** Mirrors Radix's own close timer — the grace period for crossing from an entry to its dropdown. */
var HOVER_CLOSE_DELAY_MS = 150;
/**
 * Desktop navigation menu.
 *
 * Owns hover open and close rather than leaving them to Radix. A parent entry is split into a link
 * and a separate chevron toggle, and Radix scopes both to the toggle alone — so the hover target
 * would shrink to the chevron, and merely moving off the chevron onto its own label would count as
 * leaving and close the menu. Tracking the open value here treats the whole entry and its dropdown
 * as one hover region. Clicks, keyboard, and dismissal stay with Radix.
 */
export var DesktopMenu = function (_a) {
    var menu = _a.menu, menuItemClassName = _a.menuItemClassName;
    var _b = useState(''), value = _b[0], setValue = _b[1];
    var hoverTimerRef = useRef(undefined);
    var clearHoverTimer = useCallback(function () {
        if (hoverTimerRef.current === undefined)
            return;
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = undefined;
    }, []);
    useEffect(function () { return clearHoverTimer; }, [clearHoverTimer]);
    var handleHover = useCallback(function (hoveredValue) {
        clearHoverTimer();
        // Left the entry or its dropdown. Delayed so that crossing between the two — which leaves
        // one before entering the other — does not read as leaving the menu altogether.
        if (hoveredValue === null) {
            hoverTimerRef.current = setTimeout(function () { return setValue(''); }, HOVER_CLOSE_DELAY_MS);
            return;
        }
        // Another entry is already open, so sweeping between parents should track the pointer
        // without re-waiting — the same concession Radix makes with `skipDelayDuration`.
        if (value !== '') {
            setValue(hoveredValue);
            return;
        }
        hoverTimerRef.current = setTimeout(function () { return setValue(hoveredValue); }, HOVER_OPEN_DELAY_MS);
    }, [clearHoverTimer, value]);
    // The viewport's default offset below the navbar is a margin, so the strip it creates belongs to
    // no element. A pointer crossing that strip on its way to a sub-item counts as leaving the menu,
    // closing it mid-reach. Sitting the dropdown flush against the navbar removes the gap rather
    // than papering over it.
    return (<NavigationMenu value={value} onValueChange={setValue} viewportClassName="mt-0">
      <NavigationMenuList>
        {menu.map(function (item) { return (<MenuItem key={item.key || item.title} item={item} className={menuItemClassName} onHover={handleHover}/>); })}
      </NavigationMenuList>
    </NavigationMenu>);
};
